import { CalendarClock, CalendarDays } from "lucide-react";
import { formatDueDateParts } from "@/lib/issues";
import { cn } from "@/lib/utils";

interface IssueDatePillProps {
  /** `due` is what was asked for; `target` is the latest forecast. */
  kind: "due" | "target";
  /** ISO date (no time). */
  value: string | null | undefined;
  /** `sm` is the compact chip the issue card's meta line uses. */
  size?: "default" | "sm";
  /**
   * Drop the year below 768px — `Due 20 Sept` — where a phone has no room for
   * it. The year stays in the accessible name. See [[issue-date-pill.spec]].
   */
  shortOnMobile?: boolean;
  /**
   * Colour the pill by what it says, as the issue list does: a target in the
   * In progress yellow, a due date in the overdue red once `overdue`. Off, the
   * pill is the quiet grey outline. See [[issue-date-pill.spec]] § Behavior.
   */
  tinted?: boolean;
  /**
   * The due date has passed on open work. Decided by the host, from the same
   * rule as its day count, so the pill never disagrees with the number beside
   * it. Read only by a tinted due pill.
   */
  overdue?: boolean;
}

const ICONS = { due: CalendarDays, target: CalendarClock } as const;
const LABELS = { due: "Due", target: "Target" } as const;

// Outline and text change together, never a fill. Yellow text is `warning-ink`:
// the amber fill is 1.8:1 on a white card. Overdue is the red the day count
// beside it uses. See [[theming]] § Yellow as text is `warning-ink`.
const TONES = {
  plain: "border-border text-muted-foreground",
  target: "border-warning text-warning-ink",
  overdue: "border-overdue text-overdue",
} as const;

/**
 * An issue date in an outlined pill — `Due 2 Sept 2026` with the date picker's
 * calendar, or `Target 18 Sept 2026` with a calendar-clock. Shared by the issue
 * list row, the detail page heading and the activity trail so a date reads the
 * same everywhere. Renders nothing when the date is missing or unreadable.
 * See [[issue-date-pill.spec]].
 */
export function IssueDatePill({
  kind,
  value,
  size = "default",
  shortOnMobile = false,
  tinted = false,
  overdue = false,
}: IssueDatePillProps) {
  const date = formatDueDateParts(value ?? null);
  if (!date) return null;

  const Icon = ICONS[kind];
  const isSmall = size === "sm";
  const tone = !tinted
    ? "plain"
    : kind === "target"
      ? "target"
      : overdue
        ? "overdue"
        : "plain";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full border font-medium",
        TONES[tone],
        isSmall
          ? "gap-0.5 px-2 py-0.5 text-[10px]"
          : "gap-1 px-2.5 py-1 text-[11px]",
      )}
    >
      <Icon
        className={cn("shrink-0", isSmall ? "h-2.5 w-2.5" : "h-3 w-3")}
        aria-hidden="true"
      />
      {shortOnMobile ? (
        // One inline run, so the space before the year stays ordinary text
        // rather than turning into a flex gap. Screen-reader-only on a phone,
        // not removed, so the full date is still announced.
        <span>
          {LABELS[kind]} {date.dayMonth}
          <span className="max-md:sr-only"> {date.year}</span>
        </span>
      ) : (
        <>
          {LABELS[kind]} {date.dayMonth} {date.year}
        </>
      )}
    </span>
  );
}
